// web/src/features/pm/PmDetail.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { collection, getDocs, orderBy, query } from 'firebase/firestore'

import { listCommunitiesFS } from '@/services/firestore/communityService'
import {
  createPmTemplateFS,
  getPmTemplateFS,
  updatePmTemplateFS
} from '@/services/firestore/pmService'
import { subscribeSysConfigFS } from '@/services/firestore/sysConfigService'
import { db } from '@/firebase'
import { getSession } from '@/lib/auth'
import useSession from '@/hooks/useSession'

import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const DEFAULT_SCHEDULE = {
  frequency: 'monthly', // 'weekly' | 'monthly' | 'quarterly' | 'yearly'
  interval: 1,
  dayOfMonth: 1,
  startDate: '',
  endDate: '',
  timeOfDay: '06:00',
  estimatedMinutes: 60,
  workOrderLeadDays: 0
}

const DEFAULT_WORK_ORDER_DEFAULTS = {
  propertyId: null,
  buildingName: '',
  unitNumber: '',
  maintenanceLocationId: null,
  maintenanceCategoryId: '413586',
  maintenanceProblemId: '412942',
  subMaintenanceProblemId: null,
  maintenancePriorityId: null,
  permissionToEnter: '0',
  isFloatingWorkOrder: '1',
  assignedTo: null,
  petsDescription: '',
  priorityOrderNum: 0
}

const DEFAULT_ENTRATA_SYNC = {
  status: 'pending', // 'pending' | 'in_progress' | 'synced' | 'error'
  lastAttempt: null,
  lastStatusMessage: ''
}

/* ---------- shared helpers ---------- */

function lc(s) {
  return (s || '').toString().trim().toLowerCase()
}

/* ---------- date helpers for schedule preview ---------- */

function parseDateTime(dateStr, timeStr) {
  if (!dateStr) return null

  let y, m, d
  if (dateStr.includes('-')) {
    ;[y, m, d] = dateStr.split('-').map(Number)
  } else if (dateStr.includes('/')) {
    ;[m, d, y] = dateStr.split('/').map(Number)
  }

  if (!y || !m || !d) return null
  const [hh, mm] = (timeStr || '06:00').split(':').map(Number)
  return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0)
}

function getDayOfMonthFromDate(dateStr) {
  const dt = parseDateTime(dateStr, '00:00')
  return dt ? dt.getDate() : 1
}

// Compare by DATE ONLY (local), inclusive.
// If today is the endDate (or after), template is considered ended.
function isOnOrAfterEndDate(endDateStr) {
  if (!endDateStr) return false
  // endDateStr expected as YYYY-MM-DD
  const parts = endDateStr.split('-').map(Number)
  if (parts.length !== 3) return false
  const [y, m, d] = parts
  if (!y || !m || !d) return false

  const today = new Date()
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0)
  const endStart = new Date(y, m - 1, d, 0, 0, 0, 0)

  return todayStart.getTime() >= endStart.getTime()
}

function addInterval(base, frequency, interval) {
  const iv = Number(interval) || 1
  const d = new Date(base.getTime())
  switch ((frequency || 'monthly').toLowerCase()) {
    case 'weekly':
      d.setDate(d.getDate() + 7 * iv)
      break
    case 'quarterly':
      d.setMonth(d.getMonth() + 3 * iv)
      break
    case 'yearly':
      d.setFullYear(d.getFullYear() + iv)
      break
    case 'monthly':
    default:
      d.setMonth(d.getMonth() + iv)
      break
  }
  return d
}

function computeSchedulePreview(schedule) {
  if (!schedule) return null

  const firstPm = parseDateTime(schedule.startDate, schedule.timeOfDay)
  if (!firstPm) return null

  const leadDays = Number(schedule.workOrderLeadDays) || 0
  const firstWo = new Date(firstPm.getTime())
  if (leadDays) firstWo.setDate(firstWo.getDate() - leadDays)

  const endPm = schedule.endDate
    ? parseDateTime(schedule.endDate, schedule.timeOfDay)
    : null

  const now = new Date()

  // If we've already passed the end date, no future occurrences
  if (endPm && now > endPm) {
    return {
      firstPm,
      firstWo,
      nextPmFromToday: null,
      nextWoFromToday: null
    }
  }

  let nextPmFromToday

  if (now < firstPm) {
    // "Next PM occurrence (from today)" = the one AFTER the first
    nextPmFromToday = addInterval(firstPm, schedule.frequency, schedule.interval)
  } else {
    nextPmFromToday = new Date(firstPm.getTime())
    let safety = 0
    while (nextPmFromToday <= now && safety < 600) {
      nextPmFromToday = addInterval(nextPmFromToday, schedule.frequency, schedule.interval)
      safety++
    }
  }

  // Clamp by end date, if present
  if (endPm && nextPmFromToday && nextPmFromToday > endPm) {
    nextPmFromToday = null
  }

  let nextWoFromToday = null
  if (nextPmFromToday) {
    nextWoFromToday = new Date(nextPmFromToday.getTime())
    if (leadDays) {
      nextWoFromToday.setDate(nextWoFromToday.getDate() - leadDays)
    }
  }

  return { firstPm, firstWo, nextPmFromToday, nextWoFromToday }
}

function formatPreviewDate(dt) {
  if (!dt || Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

/* ---------- timeline normalization (for table view) ---------- */

function getEntrataRefIdFromEntry(entry) {
  if (!entry || typeof entry !== 'object') return ''
  const meta = entry.meta || entry.details || {}
  return (
    entry.entrataRefId ||
    entry.entrataRef ||
    entry.workOrderRefId ||
    entry.woRef ||
    entry.refId ||
    meta.entrataRefId ||
    meta.entrataRef ||
    meta.workOrderRefId ||
    meta.refId ||
    ''
  )
}

function normalizeTimelineEntry(entry) {
  if (!entry) {
    return { event: '', user: '', status: '', when: null }
  }

  const rawEvent = String(entry.event || '')
  const parts = rawEvent.split('—')
  let eventBase = parts[0]?.trim() || rawEvent
  const userFromEvent = parts.slice(1).join('—').trim()

  const refId = getEntrataRefIdFromEntry(entry)
  const pmNumber = entry.pmNumber || ''

  const lower = eventBase.toLowerCase()

  if (lower.startsWith('pm sent to entrata')) {
    const bits = [eventBase]
    if (pmNumber) bits.push(pmNumber)
    if (refId) bits.push(`Entrata Ref: ${refId}`)
    eventBase = bits.join(' — ')
  } else if (
    lower.startsWith('pm template created') ||
    lower.startsWith('pm template updated')
  ) {
    if (pmNumber) {
      eventBase = `${eventBase} — ${pmNumber}`
    }
  }

  return {
    event: eventBase,
    user: (entry.by || userFromEvent || '').trim(),
    status: (entry.status || '').trim(),
    when: entry.ts || entry.when || entry.createdAt || null
  }
}

export default function PmDetail() {
  const { pmId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const isNew = !pmId || pmId === 'new'

  const [communities, setCommunities] = useState([])
  const [communityId, setCommunityId] = useState(searchParams.get('communityId') || '')

  const [facilityTypes, setFacilityTypes] = useState([])

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const [form, setForm] = useState({
    name: '',
    description: '',
    isActive: true,
    facilityType: '',
    schedule: DEFAULT_SCHEDULE,
    workOrderDefaults: DEFAULT_WORK_ORDER_DEFAULTS,
    entrataSync: DEFAULT_ENTRATA_SYNC
  })

  const [timeline, setTimeline] = useState([])
  const [certificationCoordination, setCertificationCoordination] = useState(null)
  const [timelineOpen, setTimelineOpen] = useState(false)
  const [showWorkOrderMapping, setShowWorkOrderMapping] = useState(false)

  /* ---------- session + scoped community codes ---------- */

  const sess = useSession()
  const myCommunities = useMemo(
    () => {
      if (sess?.allCommunities === true) return new Set()
      return new Set(
        (Array.isArray(sess?.communities) ? sess.communities : [])
          .filter(Boolean)
          .map(lc)
      )
    },
    [sess]
  )

  useEffect(() => {
    return subscribeSysConfigFS((next) => {
      setShowWorkOrderMapping(next?.entrata?.showPmWorkOrderMapping === true)
    })
  }, [])

  /* ---------- derived: schedule preview ---------- */

  const schedulePreview = useMemo(() => computeSchedulePreview(form.schedule), [form.schedule])

  // Derived: has this template ended as of today?
  const endedByDate = useMemo(
    () => isOnOrAfterEndDate(form.schedule?.endDate || ''),
    [form.schedule?.endDate]
  )

  /* ---------- Load communities ---------- */

  useEffect(() => {
    let mounted = true
    async function loadCommunities() {
      try {
        const list = await listCommunitiesFS()
        if (!mounted) return

        const allCommunities = Array.isArray(list) ? list : []

        // Sort alphabetically by display name (stable, case-insensitive)
        allCommunities.sort((a, b) => {
          const nameA = (a.communityName || a.name || a.code || '').toString().toLowerCase()
          const nameB = (b.communityName || b.name || b.code || '').toString().toLowerCase()
          return nameA.localeCompare(nameB)
        })

        let scoped = allCommunities.filter(c => {
          if (!myCommunities.size) return true
          const code = lc(c.code || c.communityCode)
          const name = lc(c.communityName || c.name || c.code)
          return myCommunities.has(code) || myCommunities.has(name)
        })
        if (!scoped.length) scoped = allCommunities

        setCommunities(scoped)

        // IMPORTANT:
        // Do NOT auto-select the first community for new PMs.
        // We want "Select community" pinned until the user chooses.
      } catch (err) {
        console.error('Error loading communities', err)
        if (mounted) setError('Failed to load communities')
      }
    }
    loadCommunities()
    return () => {
      mounted = false
    }
  }, [myCommunities])

  /* ---------- Load facility types ---------- */

  useEffect(() => {
    let mounted = true
    async function loadFacilityTypes() {
      try {
        const col = collection(db, 'facilityTypes')
        const q = query(col, orderBy('name', 'asc'))
        const snap = await getDocs(q)
        if (!mounted) return
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        setFacilityTypes(list)
      } catch (err) {
        console.error('Error loading facility types', err)
      }
    }
    loadFacilityTypes()
    return () => {
      mounted = false
    }
  }, [])

  /* ---------- Load PM template when editing ---------- */

  useEffect(() => {
    let mounted = true
    async function loadPmTemplate() {
      if (isNew) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        const pm = await getPmTemplateFS(pmId)
        if (!pm) {
          if (mounted) {
            setError('PM template not found')
            setLoading(false)
          }
          return
        }

        const pmCommunityId = pm.communityId || communityId

        if (mounted) {
          setCommunityId(pmCommunityId || '')
          setCertificationCoordination(pm.certificationCoordination || null)

          setForm({
            name: pm.name || '',
            description: pm.description || '',
            isActive: pm.isActive !== false,
            facilityType: pm.facilityType || '',
            schedule: { ...DEFAULT_SCHEDULE, ...(pm.schedule || {}) },
            workOrderDefaults: {
              ...DEFAULT_WORK_ORDER_DEFAULTS,
              ...(pm.workOrderDefaults || {})
            },
            entrataSync:
              pm.entrataSync && typeof pm.entrataSync === 'object'
                ? {
                    ...DEFAULT_ENTRATA_SYNC,
                    ...pm.entrataSync,
                    status: pm.entrataSync.status || 'pending'
                  }
                : { ...DEFAULT_ENTRATA_SYNC }
          })

          setTimeline(Array.isArray(pm.timeline) ? pm.timeline : [])
        }
      } catch (err) {
        console.error('Error loading PM template', err)
        if (mounted) setError('Failed to load PM template')
      } finally {
        if (mounted) setLoading(false)
      }
    }
    loadPmTemplate()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmId, isNew])

  const selectedCommunity = useMemo(
    () => communities.find(c => (c.id || c.communityId) === communityId),
    [communities, communityId]
  )

  /* ---------- Local state helpers ---------- */

  function updateField(field, value) {
    setForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  function updateScheduleField(field, value) {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value
      }
    }))
  }

  function updateWOField(field, value) {
    setForm(prev => ({
      ...prev,
      workOrderDefaults: {
        ...prev.workOrderDefaults,
        [field]: value
      }
    }))
  }

  function handleCommunityChange(e) {
    const value = e.target.value
    setCommunityId(value || '')
  }

  /* ---------- Save / Cancel ---------- */

  async function handleSave(e) {
    e.preventDefault()

    const name = (form.name || '').trim()
    const description = (form.description || '').trim()
    const facilityType = form.facilityType || ''
    const startDate = form.schedule.startDate || ''
    const effectiveCommunityId = communityId || ''

    if (!name || !facilityType || !effectiveCommunityId || !description || !startDate) {
      setError(
        'Please complete all required fields: Title, Facility Type, Community, Description, and Start Date.'
      )
      return
    }

    // End Date behavior:
    // If today is on/after endDate, force inactive.
    const endDateStr = (form.schedule?.endDate || '').trim()
    const forceInactiveBecauseEnded = isOnOrAfterEndDate(endDateStr)

    const primaryCommunity =
      communities.find(c => (c.id || c.communityId) === effectiveCommunityId) || {}

    const communityCode = primaryCommunity.code || primaryCommunity.communityCode || ''
    const communityName = primaryCommunity.communityName || primaryCommunity.name || communityCode

    let userLabel = 'System'
    try {
      const u = typeof getSession === 'function' ? getSession() : null
      if (u) {
        const uName = u.displayName || u.email || 'System'
        const email = u.email || ''
        userLabel = email ? `${uName} (${email})` : uName
      }
    } catch {
      // ignore
    }

    const nowIso = new Date().toISOString()
    const eventLabel = isNew ? 'PM template created' : 'PM template updated'
    const statusLabel = isNew ? 'created' : 'updated'

    const existingTimeline = Array.isArray(timeline) ? timeline : []
    const nextTimeline = [
      ...existingTimeline,
      {
        event: eventLabel,
        by: userLabel,
        status: statusLabel,
        ts: nowIso
      }
    ]

    try {
      setSaving(true)
      setError('')

      const normalizedEntrataSync =
        form.entrataSync && typeof form.entrataSync === 'object'
          ? {
              ...DEFAULT_ENTRATA_SYNC,
              ...form.entrataSync,
              status: form.entrataSync.status || 'pending'
            }
          : { ...DEFAULT_ENTRATA_SYNC }

      const basePayload = {
        name,
        description,
        // force inactive if ended
        isActive: forceInactiveBecauseEnded ? false : !!form.isActive,
        facilityType,
        schedule: {
          ...form.schedule,
          startDate,
          endDate: endDateStr || '',
          dayOfMonth: getDayOfMonthFromDate(startDate),
          interval: Number(form.schedule.interval) || 1,
          estimatedMinutes: Number(form.schedule.estimatedMinutes) || 60,
          workOrderLeadDays: Number(form.schedule.workOrderLeadDays) || 0
        },
        communityIds: [effectiveCommunityId],
        communityId: effectiveCommunityId,
        communityCode,
        communityName,
        workOrderDefaults: {
          ...form.workOrderDefaults,
          propertyId:
            form.workOrderDefaults.propertyId ??
            selectedCommunity?.entrataCommunityId ??
            null,
          maintenanceLocationId: form.workOrderDefaults.maintenanceLocationId
            ? Number(form.workOrderDefaults.maintenanceLocationId)
            : null,
          maintenanceCategoryId: form.workOrderDefaults.maintenanceCategoryId
            ? Number(form.workOrderDefaults.maintenanceCategoryId)
            : null,
          maintenanceProblemId: form.workOrderDefaults.maintenanceProblemId
            ? Number(form.workOrderDefaults.maintenanceProblemId)
            : null,
          subMaintenanceProblemId: form.workOrderDefaults.subMaintenanceProblemId
            ? Number(form.workOrderDefaults.subMaintenanceProblemId)
            : null,
          maintenancePriorityId: form.workOrderDefaults.maintenancePriorityId
            ? Number(form.workOrderDefaults.maintenancePriorityId)
            : null,
          assignedTo: form.workOrderDefaults.assignedTo
            ? Number(form.workOrderDefaults.assignedTo)
            : null,
          priorityOrderNum: form.workOrderDefaults.priorityOrderNum
            ? Number(form.workOrderDefaults.priorityOrderNum)
            : 0
        },
        entrataSync: normalizedEntrataSync,
        timeline: nextTimeline
      }

      if (isNew) {
        await createPmTemplateFS({ ...basePayload })
      } else {
        await updatePmTemplateFS(pmId, { ...basePayload })
      }

      setTimeline(nextTimeline)

      navigate(`/pm?communityId=${encodeURIComponent(communityCode || '')}`)
    } catch (err) {
      console.error('[PmDetail] save failed', err)
      setError('Failed to save PM template')
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    setShowCancelConfirm(true)
  }

  function confirmCancel() {
    setShowCancelConfirm(false)
    const targetCommunityId = communityId || ''
    navigate(`/pm?communityId=${encodeURIComponent(targetCommunityId)}`)
  }

  /* ---------- Render ---------- */

  if (loading) {
    return (
      <div>
        <div style={{ height: 24 }} />
        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: '#444' }}>
          {isNew ? 'New Preventive Maintenance Item' : 'Edit Preventive Maintenance Item'}
        </h2>
        <p>Loading…</p>
      </div>
    )
  }

  return (
    <div>
      <h2
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 18,
          marginBottom: 22,
          fontSize: 26,
          fontWeight: 600,
          color: '#444'
        }}
      >
        <span>{isNew ? 'New Preventive Maintenance Item' : 'Edit Preventive Maintenance Item'}</span>
        <span style={{ flex: 1 }} />
      </h2>

      {error && (
        <div
          className="card"
          style={{
            borderColor: '#b94a48',
            background: '#fff7f7',
            color: '#b94a48',
            marginTop: 12,
            marginBottom: 8
          }}
        >
          {error}
        </div>
      )}

      {endedByDate && (
        <div
          className="card"
          style={{
            borderColor: '#c62828',
            background: '#fff7f7',
            color: '#c62828',
            marginTop: 12,
            marginBottom: 8
          }}
        >
          This PM has reached its End Date. It will be saved as <strong>Inactive</strong> and will not generate/sync future work.
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>PM Basics</h3>

          <div
            className="mobile-stack-grid form-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16
            }}
          >
            <label style={{ flex: 1 }}>
              Title (required)
              <input
                type="text"
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                required
              />
            </label>

            <label style={{ flex: 1 }}>
              Facility Type (required)
              <select
                value={form.facilityType}
                onChange={e => updateField('facilityType', e.target.value)}
                required
              >
                <option value="">Select facility type</option>
                {facilityTypes.map(ft => {
                  const value = ft.name || ft.label || ft.value || ft.id
                  const key = ft.id || value
                  if (!value) return null
                  return (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  )
                })}
              </select>
            </label>
          </div>

          <div
            className="mobile-stack-grid form-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginTop: 12
            }}
          >
            <label style={{ flex: 1 }}>
              Community (required)
              <select value={communityId} onChange={handleCommunityChange} required>
                {/* pinned */}
                <option value="">Select community</option>

                {/* already sorted during load */}
                {communities.map(c => {
                  const id = c.id || c.communityId
                  const name = c.communityName || c.name || id
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  )
                })}
              </select>
              <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                PM template is scoped to a single community.
              </div>
            </label>

            <div style={{ flex: 1 }} />
          </div>

          <div className="form-row" style={{ marginTop: 12 }}>
            <label style={{ flex: 1 }}>
              Description (required)
              <textarea
                value={form.description}
                onChange={e => updateField('description', e.target.value)}
                rows={3}
                required
              />
            </label>
          </div>

          {showWorkOrderMapping && (
            <div
              className="mobile-stack-grid form-row"
              style={{
                marginTop: 16,
                borderTop: '1px solid #eee',
                paddingTop: 12,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16
              }}
            >
              <div style={{ gridColumn: '1 / span 2', marginBottom: 4 }}>
                <strong>Entrata Work Order Mapping</strong>
                <div className="muted" style={{ fontSize: 12 }}>
                  Entrata API fields used when syncing PMs to Entrata work orders.
                </div>
              </div>

              <label>
                Building Name (Entrata)
                <input
                  type="text"
                  value={form.workOrderDefaults.buildingName}
                  onChange={e => updateWOField('buildingName', e.target.value)}
                />
              </label>

              <label>
                Unit Number (Entrata)
                <input
                  type="text"
                  value={form.workOrderDefaults.unitNumber}
                  onChange={e => updateWOField('unitNumber', e.target.value)}
                />
              </label>

              <label>
                Maintenance Location ID (Entrata)
                <input
                  type="number"
                  value={form.workOrderDefaults.maintenanceLocationId || ''}
                  onChange={e => updateWOField('maintenanceLocationId', e.target.value)}
                />
              </label>

              <label>
                Permission To Enter (Entrata)
                <select
                  value={form.workOrderDefaults.permissionToEnter}
                  onChange={e => updateWOField('permissionToEnter', e.target.value)}
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </label>

              <label>
                Floating Work Order (Entrata)
                <select
                  value={form.workOrderDefaults.isFloatingWorkOrder}
                  onChange={e => updateWOField('isFloatingWorkOrder', e.target.value)}
                >
                  <option value="1">Yes (Unassigned to lease)</option>
                  <option value="0">No</option>
                </select>
              </label>

              <label style={{ gridColumn: '1 / span 2' }}>
                Pets Description (Entrata)
                <input
                  type="text"
                  value={form.workOrderDefaults.petsDescription}
                  onChange={e => updateWOField('petsDescription', e.target.value)}
                />
              </label>
            </div>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Schedule</h3>
          {certificationCoordination&&<p role="status"><strong>Certification coordination brought forward to {certificationCoordination.opensOn}</strong> · Complete by {certificationCoordination.deadline}. This uses the existing {certificationCoordination.originalDate} occurrence; it does not create another work order for that date. Confirm vendor work and building coverage before booking repeat inspections.</p>}

          <div
            className="mobile-stack-grid form-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16
            }}
          >
            <label>
              Frequency
              <select
                value={form.schedule.frequency}
                onChange={e => updateScheduleField('frequency', e.target.value)}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>

            <label>
              Interval
              <input
                type="number"
                min="1"
                value={form.schedule.interval}
                onChange={e => updateScheduleField('interval', e.target.value)}
              />
              <div className="muted" style={{ fontSize: 12 }}>
                Interval works with Frequency (e.g., every 3 months).
              </div>
            </label>

            <label>
              Start Date (required)
              <input
                type="date"
                value={form.schedule.startDate}
                onChange={e => updateScheduleField('startDate', e.target.value)}
                required
              />
            </label>

            <label>
              End Date (optional)
              <input
                type="date"
                value={form.schedule.endDate || ''}
                onChange={e => updateScheduleField('endDate', e.target.value)}
              />
              <div className="muted" style={{ fontSize: 12 }}>
                If End Date is today or earlier, this PM will be saved as Inactive.
              </div>
            </label>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 18,
                opacity: endedByDate ? 0.6 : 1
              }}
              data-tooltip="When unchecked, this PM template will not generate future PM work or Entrata work orders, but existing history is kept."
            >
              <input
                type="checkbox"
                checked={endedByDate ? false : !!form.isActive}
                onChange={e => updateField('isActive', e.target.checked)}
                disabled={endedByDate}
              />
              Active
              <span className="muted" style={{ fontSize: 12 }}>
                (Active PMs generate future tasks &amp; work orders.)
              </span>
            </label>
          </div>

          {schedulePreview && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#555' }}>
              <div>
                <strong>First work order will be created:</strong>{' '}
                {formatPreviewDate(schedulePreview.firstWo)}
              </div>
              <div>
                <strong>First PM occurrence:</strong>{' '}
                {formatPreviewDate(schedulePreview.firstPm)}
              </div>
              <div>
                <strong>Next work order will be created:</strong>{' '}
                {formatPreviewDate(schedulePreview.nextWoFromToday)}
              </div>
              <div>
                <strong>Next PM occurrence (from today):</strong>{' '}
                {formatPreviewDate(schedulePreview.nextPmFromToday)}
              </div>
            </div>
          )}
        </div>

        <div className="form-actions" style={{ marginTop: 0, marginBottom: 16, display: 'flex', gap: 8 }}>
          <button type="submit" disabled={saving} className="btn primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>

      <div className="card" style={{ marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}
        >
          <h3 style={{ margin: 0 }}>
            Timeline <span className="muted">({Array.isArray(timeline) ? timeline.length : 0})</span>
          </h3>
          <button
            type="button"
            className="btn secondary"
            onClick={() => setTimelineOpen(o => !o)}
            aria-expanded={timelineOpen}
          >
            {timelineOpen ? 'Hide' : 'Show'}
          </button>
        </div>

        {timelineOpen && (
          <>
            {(!timeline || !timeline.length) && (
              <p className="muted" style={{ marginTop: 8 }}>
                No activity yet. The timeline will update when this PM is created and edited.
              </p>
            )}
            {Array.isArray(timeline) && timeline.length > 0 && (
              <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table
                className="table"
                style={{
                  marginTop: 12,
                  tableLayout: 'fixed',
                  wordWrap: 'break-word'
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: '45%' }}>Event</th>
                    <th style={{ width: '25%' }}>User</th>
                    <th style={{ width: '15%' }}>When</th>
                    <th style={{ width: '15%' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline
                    .slice()
                    .sort((a, b) => {
                      const da = new Date(a.ts || a.when || 0).getTime()
                      const db = new Date(b.ts || b.when || 0).getTime()
                      return db - da
                    })
                    .map((raw, idx) => {
                      const t = normalizeTimelineEntry(raw)
                      const dt = t.when ? new Date(t.when) : null
                      return (
                        <tr key={idx}>
                          <td>{t.event || '—'}</td>
                          <td>{t.user || '—'}</td>
                          <td>{dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleString() : '—'}</td>
                          <td>{t.status || '—'}</td>
                        </tr>
                      )
                    })}
                </tbody>
              </table></div>
            )}
          </>
        )}
      </div>

      {showCancelConfirm && (
        <ConfirmDialog
          open={showCancelConfirm}
          title="Discard changes?"
          message="Are you sure you want to leave this page? Unsaved changes will be lost."
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={confirmCancel}
        />
      )}
    </div>
  )
}
